import { PageHead } from '@components/PageHead'
import {
  useReportsData,
  type UseReportsDataOptions,
  type ReportsResult,
} from '@features/vault/hooks/useReportsData'
import { type ReportItem } from '@features/vault/services/reportAnalysis'
import { EmptyState } from '@shared/ui/EmptyState'
import { Notice } from '@shared/ui/Notice'
import { Icon, type IconName } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * セキュリティレポート画面 (T-D-Reports、計画書 §4.6)。
 *
 * 設計判断:
 *   - `reports` → カードグリッド (4 種レポート + カウント表示)
 *   - `reports/:type` → 詳細リスト (該当アイテム + 修正ボタン)
 *   - 弱・再利用・非暗号化・2FA 未設定はすべて reportAnalysis.ts でクライアント側分析。
 *     外部 API / ドメインへの通信は行わない (ゼロ知識方針)。
 */

type ReportKey = 'reused' | 'weak' | 'unsecured' | 'inactive-2fa'
type ReportTone = 'danger' | 'warn' | ''

interface ReportDef {
  readonly key: ReportKey
  readonly icon: IconName
  readonly tone: ReportTone
  readonly labelKey: string
  readonly subKey: string
  readonly stubKey?: string
  readonly reasonKey: string
  readonly isStub: boolean
  readonly getItems: (result: ReportsResult) => readonly ReportItem[]
}

const REPORT_DEFS: readonly ReportDef[] = [
  {
    key: 'reused',
    icon: 'refresh',
    tone: 'warn',
    labelKey: 'reports.card.reused.label',
    subKey: 'reports.card.reused.sub',
    reasonKey: 'reports.reason.reused',
    isStub: false,
    getItems: r => r.reused,
  },
  {
    key: 'weak',
    icon: 'key',
    tone: 'warn',
    labelKey: 'reports.card.weak.label',
    subKey: 'reports.card.weak.sub',
    reasonKey: 'reports.reason.weak',
    isStub: false,
    getItems: r => r.weak,
  },
  {
    key: 'unsecured',
    icon: 'unlock',
    tone: '',
    labelKey: 'reports.card.unsecured.label',
    subKey: 'reports.card.unsecured.sub',
    reasonKey: 'reports.reason.unsecured',
    isStub: false,
    getItems: r => r.unsecured,
  },
  {
    key: 'inactive-2fa',
    icon: 'shield',
    tone: '',
    labelKey: 'reports.card.inactive_2fa.label',
    subKey: 'reports.card.inactive_2fa.sub',
    reasonKey: 'reports.reason.inactive_2fa',
    isStub: false,
    getItems: r => r.inactive2fa,
  },
]

const iconClass = (tone: ReportTone): string => {
  if (tone === 'danger') return 'report-card-icon report-card-icon--danger'
  if (tone === 'warn') return 'report-card-icon report-card-icon--warn'

  return 'report-card-icon report-card-icon--neutral'
}

export interface ReportsPageProps {
  route: string
  reportsDataOptions?: UseReportsDataOptions
}

export const ReportsPage = ({ route, reportsDataOptions }: ReportsPageProps): JSX.Element => {
  const { t } = useTranslation()
  const state = useReportsData(reportsDataOptions)

  const subKey = route !== 'reports' ? (route.split('/')[1] ?? '') : ''
  const def = subKey ? REPORT_DEFS.find(d => d.key === subKey) : undefined

  if (state.kind === 'error') {
    return (
      <div>
        <PageHead title={t('reports.title')} sub={t('reports.sub')} />
        <div className="page-body">
          <Notice tone="danger" ico="alert">
            {t(state.messageKey)}
          </Notice>
        </div>
      </div>
    )
  }

  if (def) {
    const items = state.kind === 'ready' ? def.getItems(state.result) : []
    const isDanger = def.tone === 'danger'

    return (
      <div>
        <PageHead
          title={t(def.labelKey)}
          sub={t(def.subKey)}
          actions={
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                window.location.hash = 'reports'
              }}
            >
              <Icon name="arrow-left" aria-hidden="true" />
              {t('reports.back')}
            </button>
          }
        />
        <div className="page-body">
          {state.kind === 'loading' && (
            <div className="card">
              <div className="home-card-empty">
                <EmptyState icon="shield" title="…" sub="" />
              </div>
            </div>
          )}
          {state.kind === 'ready' && (
            <>
              {isDanger && items.length > 0 && (
                <Notice tone="danger" ico="alert">
                  <strong>{t('reports.detail.danger_notice', { count: items.length })}</strong>
                </Notice>
              )}
              {items.length === 0 ? (
                <EmptyState
                  icon="check"
                  title={t('reports.detail.empty_title')}
                  sub={t('reports.detail.empty_sub', { label: t(def.labelKey) })}
                />
              ) : (
                <div
                  className={`card${isDanger && items.length > 0 ? ' report-detail-list--spaced' : ''}`}
                >
                  <div className="report-list-head">
                    <div>{t('reports.detail.col_item')}</div>
                    <div>{t('reports.detail.col_username')}</div>
                    <div>{t('reports.detail.col_reason')}</div>
                  </div>
                  {items.map(item => (
                    <div
                      key={item.id}
                      className="report-list-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        window.location.hash = `vault/${item.id}`
                      }}
                      onKeyDown={ev => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          ev.preventDefault()
                          window.location.hash = `vault/${item.id}`
                        }
                      }}
                      aria-label={item.name}
                    >
                      <div>
                        <div className="nm">{item.name}</div>
                        {item.url && <div className="sb">{item.url}</div>}
                      </div>
                      <div className="sb mono">{item.username ?? '—'}</div>
                      <div className="report-list-actions">
                        <span className={`tag${isDanger ? ' danger' : ''}`}>
                          {t(def.reasonKey)}
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={ev => {
                            ev.stopPropagation()
                            window.location.hash = `vault/${item.id}/edit`
                          }}
                        >
                          {t('reports.detail.fix')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  // Top — card grid
  return (
    <div>
      <PageHead title={t('reports.title')} sub={t('reports.sub')} />
      <div className="page-body">
        <div className="report-grid">
          {REPORT_DEFS.map(rep => {
            const count = state.kind === 'ready' ? rep.getItems(state.result).length : null

            return (
              <div
                key={rep.key}
                className="card report-card"
                role="button"
                tabIndex={0}
                onClick={() => {
                  window.location.hash = `reports/${rep.key}`
                }}
                onKeyDown={ev => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault()
                    window.location.hash = `reports/${rep.key}`
                  }
                }}
                aria-label={t(rep.labelKey)}
              >
                <div className="report-card-header">
                  <div className={iconClass(rep.tone)}>
                    <Icon name={rep.icon} aria-hidden="true" />
                  </div>
                  <span className="report-card-arrow" aria-hidden="true">
                    <Icon name="arrow-right" />
                  </span>
                </div>
                <div
                  className={`report-card-count${rep.tone === 'danger' && (count ?? 0) > 0 ? ' report-card-count--danger' : ''}`}
                >
                  {count === null ? '…' : count}
                </div>
                <div className="report-card-label">{t(rep.labelKey)}</div>
                <div className="report-card-sub">
                  {rep.isStub ? (
                    <span className="tag">{rep.stubKey ? t(rep.stubKey) : ''}</span>
                  ) : (
                    t(rep.subKey)
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
